// src/pages/superadmin/SuperAdminStaff.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Plus, Edit2, Trash2, Search, X, CheckCircle, XCircle,
  Mail, Phone, User, Shield, RefreshCw, Key
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuperAdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'sysadmin',
    phone: '',
    is_active: true,
    password: ''
  });

  useEffect(() => {
    loadStaff();
  }, [searchTerm]);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/staff/`, {
        params: { limit: 100, search: searchTerm }
      });
      setStaff(response.data || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      alert('Email, first name and last name are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingStaff) {
        await axios.put(`/staff/${editingStaff.id}`, {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          phone: formData.phone,
          is_active: formData.is_active
        });
      } else {
        if (!formData.password) {
          alert('Password is required for new staff');
          setSubmitting(false);
          return;
        }
        await axios.post('/staff/', {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          phone: formData.phone,
          password: formData.password,
          is_active: formData.is_active
        });
      }
      setShowModal(false);
      resetForm();
      await loadStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      alert(error.response?.data?.detail || 'Error saving staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (window.confirm(`Delete staff "${staffMember.first_name} ${staffMember.last_name}"?`)) {
      try {
        await axios.delete(`/staff/${staffMember.id}`);
        await loadStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
        alert('Error deleting staff');
      }
    }
  };

  const handleResetPassword = async (staffMember) => {
    setSelectedStaff(staffMember);
    setNewPassword('');
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    if (!newPassword) {
      alert('Please enter a new password');
      return;
    }
    try {
      await axios.post(`/auth/reset-password/${selectedStaff.id}`, {
        new_password: newPassword,
        force: true
      });
      setResetResult({
        staff: `${selectedStaff.first_name} ${selectedStaff.last_name}`,
        password: newPassword
      });
      setShowResetModal(false);
      setTimeout(() => setResetResult(null), 10000);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'sysadmin',
      phone: '',
      is_active: true,
      password: ''
    });
    setEditingStaff(null);
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email || '',
      first_name: staffMember.first_name || '',
      last_name: staffMember.last_name || '',
      role: staffMember.role || 'sysadmin',
      phone: staffMember.phone || '',
      is_active: staffMember.is_active !== false,
      password: ''
    });
    setShowModal(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { label: 'Super Admin', color: 'bg-red-500/20 text-red-400', icon: Shield },
      sysadmin: { label: 'SysAdmin', color: 'bg-purple-500/20 text-purple-400', icon: Shield },
      admin: { label: 'Admin', color: 'bg-blue-500/20 text-blue-400', icon: User },
      manager: { label: 'Manager', color: 'bg-green-500/20 text-green-400', icon: User },
      staff: { label: 'Staff', color: 'bg-gray-500/20 text-gray-400', icon: User }
    };
    const c = config[role] || config.staff;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${c.color}`}>
        <Icon className="w-3 h-3" /> {c.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Staff Management</h2>
          <p className="text-gray-400 text-sm">Manage technical staff (SysAdmin level 105)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Staff
        </button>
      </div>

      {/* Reset Password Result */}
      {resetResult && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 font-medium">Password reset for {resetResult.staff}</p>
          <p className="text-white text-lg font-mono mt-1">{resetResult.password}</p>
          <p className="text-yellow-400 text-xs mt-1">⚠️ Give this password to the staff member. It won't be shown again!</p>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search staff by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white"
        />
      </div>

      {/* Staff Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr className="text-gray-400 text-sm">
                <th className="p-4 text-left">Staff</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">Role</th>
                <th className="p-4 text-left">Phone</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.filter(s => s.role === 'sysadmin' || s.role === 'super_admin').map((member) => (
                <tr key={member.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{member.first_name} {member.last_name}</p>
                      <p className="text-gray-500 text-xs">ID: {member.id.substring(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-gray-500" />
                      <span className="text-gray-300">{member.email}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {getRoleBadge(member.role)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-gray-500" />
                      <span className="text-gray-300">{member.phone || '-'}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {member.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-xs">
                        <XCircle className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleResetPassword(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400" title="Reset Password">
                        <Key className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {staff.filter(s => s.role === 'sysadmin' || s.role === 'super_admin').length === 0 && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-500">
                    No staff found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">{editingStaff ? 'Edit Staff' : 'New Staff'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email *</label>
                <input
                  type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="sysadmin">SysAdmin (Level 105) - Infrastructure</option>
                  <option value="super_admin">Super Admin (Level 110) - Full Access</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              {!editingStaff && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600"
                />
                <label className="text-sm text-gray-300">Active (can log in)</label>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50"
              >
                {submitting ? 'Saving...' : (editingStaff ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reset Password */}
      {showResetModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Reset Password</h3>
              <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-gray-300 text-sm">
                Reset password for <span className="text-white font-medium">{selectedStaff.first_name} {selectedStaff.last_name}</span>
              </p>
              <div>
                <label className="block text-sm text-gray-400 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Enter new password"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={confirmResetPassword}
                className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white font-medium"
              >
                Reset Password
              </button>
              <button
                onClick={() => setShowResetModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminStaff;